import { UserNotificationStatus } from '@automatick/shared-ts';
import {
  type PresentationActionSubmissionResponse,
  type UserNotificationActionResult,
  type UserNotificationDeliveryResponse,
  type UserNotificationPreferencesResponse,
  type UserNotificationResponse,
} from '@automatick/shared-ng';

const SUPPORTED_NOTIFICATION_STATUSES: ReadonlySet<string> = new Set([
  UserNotificationStatus.Unread,
  UserNotificationStatus.Read,
  UserNotificationStatus.ActionRequired,
  UserNotificationStatus.Completed,
  UserNotificationStatus.Dismissed,
  UserNotificationStatus.Expired,
]);

const SUPPORTED_DELIVERY_STATUSES: ReadonlySet<string> = new Set([
  'Succeeded',
  'Failed',
  'Skipped',
]);

const SUCCESSFUL_PRESENTATION_ACTION_STATUSES: ReadonlySet<string> = new Set([
  'Accepted',
  'Replayed',
]);

export const UserNotificationPreferenceKey = {
  RealtimeEnabled: 'realtimeEnabled',
  VerboseEnabled: 'verboseEnabled',
  WebEnabled: 'webEnabled',
  MobileEnabled: 'mobileEnabled',
  WatchEnabled: 'watchEnabled',
  GlassesEnabled: 'glassesEnabled',
  EmailEnabled: 'emailEnabled',
  SmsEnabled: 'smsEnabled',
} as const;

export type UserNotificationPreferenceKey =
  (typeof UserNotificationPreferenceKey)[keyof typeof UserNotificationPreferenceKey];

export type UserNotificationPreferencePatch = Readonly<
  Partial<Record<UserNotificationPreferenceKey, boolean>>
>;

export interface PresentationActionIdentity {
  readonly presentationId: string;
  readonly actionId: string;
  readonly wakeHandleId: string;
}

export type ValidUserNotificationDeliveryResponse =
  UserNotificationDeliveryResponse & {
    readonly deliveryId: string;
    readonly workspaceId: string;
    readonly notificationId: string;
    readonly destinationKind: string;
    readonly status: string;
  };

export class UserNotificationContractValidator {
  isNotification(
    value: UserNotificationResponse | null | undefined,
  ): value is UserNotificationResponse {
    return (
      !!value &&
      typeof value === 'object' &&
      this.isNonEmptyString(value.notificationId) &&
      this.isNonEmptyString(value.workspaceId) &&
      typeof value.recipientUserId === 'string' &&
      typeof value.title === 'string' &&
      typeof value.body === 'string'
    );
  }

  selectOwnedActionNotification(
    result: UserNotificationActionResult,
    workspaceId: string,
    notificationId: string,
  ): UserNotificationResponse | null {
    const notification = result.notification;
    return this.isNotification(notification) &&
      notification.workspaceId === workspaceId &&
      notification.notificationId === notificationId &&
      this.isSupportedNotificationStatus(result.status) &&
      notification.status === result.status
      ? notification
      : null;
  }

  isSuccessfulPresentationAction(
    result: PresentationActionSubmissionResponse,
    expected: PresentationActionIdentity,
  ): boolean {
    return (
      result.presentationId === expected.presentationId &&
      result.actionId === expected.actionId &&
      result.wakeHandleId === expected.wakeHandleId &&
      typeof result.status === 'string' &&
      SUCCESSFUL_PRESENTATION_ACTION_STATUSES.has(result.status)
    );
  }

  selectOwnedDeliveries(
    deliveries: readonly UserNotificationDeliveryResponse[],
    workspaceId: string,
    notificationId: string,
  ): readonly ValidUserNotificationDeliveryResponse[] {
    return deliveries.filter(
      (delivery): delivery is ValidUserNotificationDeliveryResponse =>
        this.isDelivery(delivery) &&
        delivery.workspaceId === workspaceId &&
        delivery.notificationId === notificationId &&
        SUPPORTED_DELIVERY_STATUSES.has(delivery.status),
    );
  }

  normalizeOwnedPreferences(
    preferences: UserNotificationPreferencesResponse,
    workspaceId: string,
    userId: string | null,
  ): UserNotificationPreferencesResponse | null {
    if (
      preferences.workspaceId !== workspaceId ||
      (userId !== null && preferences.userId !== userId)
    ) {
      return null;
    }

    return {
      ...preferences,
      realtimeEnabled: this.booleanOrDefault(
        preferences.realtimeEnabled,
        false,
      ),
      verboseEnabled: this.booleanOrDefault(preferences.verboseEnabled, false),
      webEnabled: this.booleanOrDefault(preferences.webEnabled, true),
      mobileEnabled: this.booleanOrDefault(preferences.mobileEnabled, false),
      watchEnabled: this.booleanOrDefault(preferences.watchEnabled, false),
      glassesEnabled: this.booleanOrDefault(preferences.glassesEnabled, false),
      emailEnabled: this.booleanOrDefault(preferences.emailEnabled, false),
      smsEnabled: this.booleanOrDefault(preferences.smsEnabled, false),
    };
  }

  private isDelivery(
    value: UserNotificationDeliveryResponse | null | undefined,
  ): value is ValidUserNotificationDeliveryResponse {
    return (
      !!value &&
      typeof value === 'object' &&
      this.isNonEmptyString(value.deliveryId) &&
      this.isNonEmptyString(value.workspaceId) &&
      this.isNonEmptyString(value.notificationId) &&
      typeof value.destinationKind === 'string' &&
      typeof value.status === 'string'
    );
  }

  private isSupportedNotificationStatus(
    value: string | null | undefined,
  ): boolean {
    return (
      typeof value === 'string' && SUPPORTED_NOTIFICATION_STATUSES.has(value)
    );
  }

  private isNonEmptyString(value: string | null | undefined): value is string {
    return typeof value === 'string' && value.trim().length > 0;
  }

  private booleanOrDefault(
    value: boolean | null | undefined,
    fallback: boolean,
  ): boolean {
    return typeof value === 'boolean' ? value : fallback;
  }
}
